import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../lib/api';
import {
  upcomingPujas as fallbackPujas,
  defaultPandits as fallbackPandits,
  samagriCatalog as fallbackSamagri,
  defaultQR,
  contactInfo,
} from '../mock';
import { useAuth } from './AuthContext';

const defaultSettings = {
  razorpayEnabled: false, razorpayKeyId: '', razorpayMode: 'test',
  upiId: '7250412100@upi', currency: 'INR',
  contactPhone: contactInfo.phone, contactEmail: contactInfo.email,
  contactAddress: contactInfo.address, whatsapp: contactInfo.whatsapp,
  social: { instagram: '', facebook: '', youtube: '', twitter: '', linkedin: '' },
  seo: {}, qr: defaultQR,
  musicEnabled: true,
  musicUrl: 'https://archive.org/download/ShivaStotrasAndMantras/13Nirvana%20Ashatakam.MP3',
  musicTitle: 'Namami Shamishan — Nirvana Shatakam',
  musicAutoplay: true,
  musicVolume: 0.35,
};

const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const { user } = useAuth();
  const [pujas, setPujas] = useState(fallbackPujas);
  const [pandits, setPandits] = useState(fallbackPandits);
  const [samagri, setSamagri] = useState(fallbackSamagri);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState(defaultSettings);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPublic = useCallback(async () => {
    try {
      const [p, pd, sm, pr, st] = await Promise.all([
        api.get('/api/pujas'),
        api.get('/api/pandits'),
        api.get('/api/samagri'),
        api.get('/api/products'),
        api.get('/api/settings'),
      ]);
      if (p.data?.length) setPujas(p.data);
      if (pd.data?.length) setPandits(pd.data);
      if (sm.data?.length) setSamagri(sm.data);
      if (pr.data) setProducts(pr.data);
      setSettings({ ...defaultSettings, ...(st.data || {}) });
    } catch (e) {
      // fall back to mock data — already set
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBookings = useCallback(async () => {
    if (!user || user === false) { setBookings([]); return; }
    try {
      const { data } = await api.get('/api/bookings');
      setBookings(data || []);
    } catch {
      setBookings([]);
    }
  }, [user]);

  useEffect(() => { fetchPublic(); }, [fetchPublic]);
  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Admin settings (returns keySecret)
  const fetchAdminSettings = async () => {
    const { data } = await api.get('/api/settings/admin');
    setSettings({ ...defaultSettings, ...(data || {}) });
    return data;
  };

  return (
    <DataContext.Provider value={{
      pujas, pandits, samagri, products, settings, bookings, loading,
      setPujas, setPandits, setSamagri, setProducts, setSettings, setBookings,
      refresh: fetchPublic, refreshBookings: fetchBookings, fetchAdminSettings,
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
